import type { ConversationMessage } from '@/features/AI-documents/types/docGen.types';
import type { ChatFlow, ChatFlowContext, ChatFlowEventResult } from '@/features/startTask/preset_task/shared/preset/chatflows/types';

export const shareCertificateFlow: ChatFlow = {
  id: 'sg-share-certificate',
  categoryMatch: ['share certificate', 'share cert'],

  getInitialMessages(preSet): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `You selected **${preSet.task.TASK_NAME}**.\n\n` +
          (docCount > 0 ? `This preset includes ${docCount} document(s).\n\n` : '') +
          `Enter a **Task Title**, select the **company**, then click **Member name** on the right to choose the shareholder in chat.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: {
        targetVarKey: '_taskTitle',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage: 'Search and select the **Singapore company** for this share certificate.',
      widget: 'entity-selector',
      widgetConfig: {
        targetVarKey: '_entity',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Entity linked. Click the **member name** in the certificate on the right (✨) to choose the shareholder — the certificate number, shares and issue date are filled from the register. Then click **Create Task**.',
      widget: null,
      nextStep: null,
    },
    {
      id: 'select-member',
      aiMessage: 'Select the **member** (shareholder) for this share certificate.',
      widget: 'officer-list',
      widgetConfig: {
        targetVarKey: '_member',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    // 스튜디오 — Registered Office 📍 주소(목록이 여러 개면 선택, 아니면 직접 입력)
    {
      id: 'address-list',
      aiMessage: 'This entity has multiple addresses. Pick the one to use for the **Registered Office**.',
      widget: 'address-options',
      awaitEvent: true,
    },
    {
      id: 'address-input',
      aiMessage: 'Please enter the **Registered Office** address. It will be filled into the certificate automatically.',
      widget: 'address-input',
      awaitEvent: true,
    },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    if (eventName === 'request-address-lookup') {
      const ctx = payload || {};
      const label = ctx.subjectLabel || 'Registered Office';
      const opts = Array.isArray(ctx.addresses) ? ctx.addresses : [];
      if (ctx.directInput || opts.length <= 1) {
        return { messages: [{ role: 'user', content: 'Enter **' + label + '** (Local Singapore or Foreign).', timestamp: Date.now(), stepId: 1 }], nextStepId: 'address-input' };
      }
      return {
        messages: [{ role: 'user', content: 'Choose an address for **' + label + '**.', timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'address-list', nextStepWidgetData: { addressOptions: opts },
      };
    }
    if (eventName === 'entity-field-select' && context.currentStepId === 'address-input') {
      const value = typeof payload === 'string' ? payload : payload?.value || payload?.address || '';
      if (!value || !String(value).trim()) return {};
      return { messages: [{ role: 'user', content: 'Entered: **' + String(value).trim() + '**', timestamp: Date.now(), stepId: 1 }], nextStepId: 'confirm' };
    }
    if (eventName === 'address-option-select') {
      const opt = payload || {};
      return { messages: [{ role: 'user', content: 'Selected: **' + (opt.label || opt.type || 'Address') + '**', timestamp: Date.now(), stepId: 1 }], nextStepId: 'confirm' };
    }
    if (eventName === 'value-submit' && context.currentStepId === 'input-title') {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [
          { role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 },
        ],
        formUpdates: { taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    if (eventName === 'entity-select' && context.currentStepId === 'select-entity') {
      const ent = payload || {};
      const entityCd = ent.entityCd || ent.icpCd || '';
      const entityName = ent.entityName || ent.companyName || 'Entity';
      if (!entityCd) return {};
      return {
        messages: [
          {
            role: 'user',
            content: `Selected: **${entityName}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          entityCd,
          entityName,
          icpCd: entityCd,
          registrationNo: ent.registrationNo || ent.regNo || '',
          companyAddress: ent.companyAddress || ent.address || '',
        },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'request-person-lookup') {
      const entityCd = context.entityCd || context.formData.entityCd || context.formData.icpCd || '';
      if (!entityCd) {
        return {
          messages: [
            {
              role: 'ai',
              content: 'Please select an entity first.',
              timestamp: Date.now(),
              stepId: 1,
              source: 'system',
            },
          ],
        };
      }
      return {
        messages: [
          {
            role: 'user',
            content: 'Looking up **member** for share certificate...',
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'select-member',
        nextStepWidgetData: {
          entityCd,
          selectedRole: 'SHARE_CERTIFICATE_MEMBER',
        },
      };
    }

    if (eventName === 'officer-select' && context.currentStepId === 'select-member') {
      const officer = payload || {};
      const officerName = officer.name || officer.contactName || 'Selected member';
      return {
        messages: [
          {
            role: 'user',
            content: `Selected member: **${officerName}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'confirm',
      };
    }

    return {};
  },
};
